import React, { memo, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CardActions,
  Box,
  useEventCallback,
} from '@mui/material';
import { FormikProps } from 'formik';
import { BatchUploadFormType } from 'system/pages/batch-management/file-managment/upload/types';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { useNavigate } from 'react-router-dom';
import SearchBatchList from 'system/components/shared/search-batch-list';
import { SearchField } from 'system/components/shared/search-batch-list/constants';
import { JobVM } from 'system/view-models/batch-managment/job-vm';
import { toast } from 'react-toastify';

interface UploadFileFormComponentProps {
  uploadFileFormContext: FormikProps<BatchUploadFormType>;
  handleAddFile: (file: File) => void;
}

const UploadFileFormComponent = ({
  uploadFileFormContext,
  handleAddFile,
}: UploadFileFormComponentProps): React.ReactElement => {
  const navigate = useNavigate();

  const isFolderNameVisible = useMemo(() => {
    return Boolean(
      uploadFileFormContext.values?.batchName?.inputFolder ||
        uploadFileFormContext.values?.batchDescription?.inputFolder
    );
  }, [
    uploadFileFormContext.values?.batchName?.inputFolder,
    uploadFileFormContext.values?.batchDescription?.inputFolder,
  ]);

  const isUploadDisabled = useMemo(() => {
    const hasValidFolderName = Boolean(uploadFileFormContext.values?.folderName);
    const batchName = uploadFileFormContext.values?.batchName;
    const batchDescription = uploadFileFormContext.values?.batchDescription;

    if (batchName && !batchName.inputFolder) {
      return true;
    }

    if (batchDescription && !batchDescription.inputFolder) {
      return true;
    }

    return !hasValidFolderName;
  }, [
    uploadFileFormContext.values?.folderName,
    uploadFileFormContext.values?.batchName,
    uploadFileFormContext.values?.batchDescription,
  ]);

  const throwJobErrorAndResetSelection = useEventCallback((job: JobVM) => {
    toast.error(`il batch ${job?.name} non necessita di file in input`);
    uploadFileFormContext.setValues({
      ...uploadFileFormContext.values,
      batchName: null,
      batchDescription: null,
      folderName: '',
    });
  });

  return (
    <>
      <Card raised className="pmi-my-3">
        <form onSubmit={uploadFileFormContext?.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography variant="h6" className="pmi-mb-3">
              Upload file
            </Typography>

            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <SearchBatchList
                    searchField={SearchField.JOB_NAME}
                    value={uploadFileFormContext.values?.batchName || null}
                    onChange={(item: JobVM | null) => {
                      if (item && !item.inputFolder) {
                        throwJobErrorAndResetSelection(item);
                        return;
                      }

                      uploadFileFormContext.setValues({
                        ...uploadFileFormContext.values,
                        batchName: item,
                        batchDescription: null,
                        folderName: item?.inputFolder || '',
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SearchBatchList
                    searchField={SearchField.JOB_DESCRIPTION}
                    value={uploadFileFormContext.values?.batchDescription || null}
                    onChange={(item: JobVM | null) => {
                      if (item && !item.inputFolder) {
                        throwJobErrorAndResetSelection(item);
                        return;
                      }

                      uploadFileFormContext.setValues({
                        ...uploadFileFormContext.values,
                        batchDescription: item,
                        batchName: null,
                        folderName: item?.inputFolder || '',
                      });
                    }}
                  />
                </Grid>
                {isFolderNameVisible ? (
                  <Grid item xs={12} sm={4}>
                    <PmiTextField
                      aria-label="folderName"
                      label="Cartella"
                      id="folderName"
                      name="folderName"
                      fullWidth
                      disabled
                      type="text"
                      variant="outlined"
                      error={
                        uploadFileFormContext.touched.folderName &&
                        Boolean(uploadFileFormContext.errors.folderName)
                      }
                      helperText={uploadFileFormContext.errors.folderName}
                      value={uploadFileFormContext.values?.folderName || ''}
                      onChange={(event) => {
                        uploadFileFormContext.setFieldValue('folderName', event?.target.value);
                      }}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <div>Carica documento&nbsp;</div>
                <PmiInputFile
                  disabled={isUploadDisabled}
                  multiple={false}
                  onChange={(file: File) => handleAddFile(file)}
                  file={uploadFileFormContext.values.file?.file || undefined}
                  showFilename
                  color="cdmBrandThird"
                  tipMaxSize={350}
                />
              </Grid>
              <Grid item xs={12} md={4} display={'flex'} alignItems={'center'}>
                <Box sx={{ mr: 1 }}>
                  <PmiButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isUploadDisabled}
                  >
                    UPLOAD
                  </PmiButton>
                </Box>
                <Box sx={{ ml: 1 }}>
                  <PmiButton
                    variant="outlined"
                    color="cdmBrand"
                    onClick={() => {
                      navigate('/sistema');
                    }}
                  >
                    Annulla
                  </PmiButton>
                </Box>
              </Grid>
            </Grid>
          </CardActions>
        </form>
      </Card>
    </>
  );
};

UploadFileFormComponent.displayName = 'Upload file form component';
const UploadFileForm = memo(UploadFileFormComponent);
export default UploadFileForm;
